'use client';
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, Cell,
} from 'recharts';
import { weeklyLeadData, Lead } from './mockLeads';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string; color: string }>;
  label?: string;
}

function CustomBarTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-3 shadow-modal">
      <p className="text-white text-xs font-600 mb-2">{label}</p>
      {payload.map((entry, i) => (
        <div key={`tooltip-entry-${i}`} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
          <span className="text-[#888] capitalize">{entry.dataKey}:</span>
          <span className="text-white font-600">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardCharts({ leads }: { leads: Lead[] }) {
  const highCount = leads.filter((l) => l.leverageScore === 'High').length;
  const medCount = leads.filter((l) => l.leverageScore === 'Medium').length;
  const lowCount = leads.filter((l) => l.leverageScore === 'Low').length;

  const radialData = [
    { name: 'High', value: highCount, fill: '#F59E0B' },
    { name: 'Medium', value: medCount, fill: '#2563EB' },
    { name: 'Low', value: lowCount, fill: '#3F3F3F' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Weekly lead volume — spans 2 cols */}
      <div className="leverage-card p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-600 text-sm">Weekly Lead Volume</h3>
            <p className="text-[#555] text-xs mt-0.5">Leads by leverage score · This week</p>
          </div>
          <span className="text-xs text-[#555] font-500">Apr 14–20, 2026</span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={weeklyLeadData} barCategoryGap="30%">
            <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} width={24} />
            <Tooltip content={<CustomBarTooltip />} />
            <Bar dataKey="high" stackId="a" fill="#F59E0B" radius={[0, 0, 0, 0]} />
            <Bar dataKey="medium" stackId="a" fill="#2563EB" />
            <Bar dataKey="low" stackId="a" fill="#2A2A2A" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 mt-3">
          {[
            { label: 'High', color: 'bg-amber-500' },
            { label: 'Medium', color: 'bg-blue-600' },
            { label: 'Low', color: 'bg-[#2A2A2A] border border-[#3A3A3A]' },
          ].map(({ label, color }) => (
            <div key={`legend-${label}`} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-sm ${color}`} />
              <span className="text-xs text-[#666]">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Score distribution */}
      <div className="leverage-card p-5">
        <h3 className="text-white font-600 text-sm mb-1">Score Distribution</h3>
        <p className="text-[#555] text-xs mb-4">All-time leverage breakdown</p>
        <div className="flex items-center justify-center">
          <ResponsiveContainer width="100%" height={160}>
            <RadialBarChart
              innerRadius="40%"
              outerRadius="80%"
              data={radialData}
              startAngle={90}
              endAngle={-270}
            >
              <RadialBar dataKey="value" cornerRadius={4}>
                {radialData.map((entry, index) => (
                  <Cell key={`radial-cell-${index}`} fill={entry.fill} />
                ))}
              </RadialBar>
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2 mt-2">
          {radialData.map((item) => (
            <div key={`dist-${item.name}`} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: item.fill }} />
                <span className="text-xs text-[#888]">{item.name} Leverage</span>
              </div>
              <span className="text-xs font-700 text-white tabular-nums">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}